import {
    AccessTime,
    Add,
    ArrowRight,
    Chat,
    Close,
    HeadsetMic,
    InfoOutlined,
    KeyboardArrowRight,
    Mail,
  } from "@mui/icons-material";
  import React, { useCallback, useMemo, useState } from "react";
  import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
  import RouteSelect from "../reusables/RouteSelect";
  import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
  import ConfigureCallActionModal from "../modals/ConfigureCallActionModal";
  import TimePicker from "react-time-picker";
  import { Info, Settings2 } from "lucide-react";
  import { Divider } from "@mui/material";
  import ConfigureCallActionForBusinessModal from "../modals/ConfigureCallActionForBusinessHours";
  import ConfigureNotificationForBusinessHours from "../modals/ConfigureNotificationForBusinessHours";
  import ConfigureNotificationForAfterHours from "../modals/ConfigureNotificationForAfterHours";
  import ConfigureCallActionForAfterModal from "../modals/ConfigureCallActionForAfterHours";
  import ConfigureNotificationModal from "../modals/ConfigureNotificationMainModal";
  import TooltipComponent from "../reusables/TooltipComponent";
  import IvrMenuOption from "./IvrMenuOption";
import OffHoursIvrMenuOption from "./OffHoursIvrMenuOption";
import { getNextAvailableIvrDigit, normalizeIvrOptionsMap, sortIvrOptionEntries } from "./routePayloadNormalize";
  
  const OffHoursIvrMenu = ({ setOpenConfigureSlotModal, route, setRoute, hasValidationError = false, enableBusinessHours = false }) => {
    React.useEffect(() => {
      const opts = route?.offHoursIvrMenu?.options;
      if (!Array.isArray(opts)) return;
      setRoute((prev) => ({
        ...prev,
        offHoursIvrMenu: { ...prev.offHoursIvrMenu, options: normalizeIvrOptionsMap(opts) },
      }));
    }, [route?.offHoursIvrMenu?.options, setRoute]);

    const [openConfigureCallActionModal, setOpenConfigureCallActionModal] =
      useState(false);
  
    const [
      openConfigureCallActionForBusinessHoursModal,
      setOpenConfigureCallActionForBusinessHoursModal,
    ] = useState(false);
  
    const [
      openConfigureCallActionForAfterHoursModal,
      setOpenConfigureCallActionForAfterHoursModal,
    ] = useState(false);
  
    const [
      openConfigureNotificationForAfterHoursModal,
      setOpenConfigureNotificationForAfterHoursModal,
    ] = useState(false);
  
    const [
      openConfigureNotificationForBusinessHoursModal,
      setOpenConfigureNotificationForBusinessHoursModal,
    ] = useState(false);
  
    const [
      openConfigureNotificationMainModal,
      setOpenConfigureNotificationMainModal,
    ] = useState(false);
  
    const routeModals = useRouteConfigModals();
    const [localRecordings] = useState(
      () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
    );
    const [localChannels] = useState(
      () => JSON.parse(localStorage.getItem("organization"))?.channels?.L || []
    );
    const recordings = routeModals?.recordings ?? localRecordings;
    const channels = routeModals?.channels ?? localChannels;

    const recordingOptions = useMemo(() => {
      if (!recordings) return [];
      return recordings.map((item) => ({ value: item.M.rec_id.S, label: item.M.title.S }));
    }, [recordings]);

    const getRecordingSelectValue = (recordingId) =>
      recordingId && recordingId !== "disabled" ? recordingId : "";

    const handleMenuRecordingChange = useCallback(
      (value) => {
        setRoute((prev) => ({
          ...prev,
          offHoursIvrMenu: { ...prev.offHoursIvrMenu, menu_recording: value || "disabled" },
        }));
      },
      [setRoute]
    );

    const handleHangupRecordingChange = useCallback(
      (value) => {
        setRoute((prev) => ({
          ...prev,
          offHoursIvrMenu: { ...prev.offHoursIvrMenu, hangup_recording: value || "disabled" },
        }));
      },
      [setRoute]
    );

    const addMenuRecordingOption = routeModals
      ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(handleMenuRecordingChange) }
      : undefined;

    const addHangupRecordingOption = routeModals
      ? { label: "Add Recording", onClick: () => routeModals.openAddRecordingModal(handleHangupRecordingChange) }
      : undefined;

    return (
      <div className={`border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm ${hasValidationError ? "bg-red-50/80" : "bg-white"}`}>
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-2 text-lg font-semibold text-gray-900">
              <Settings2 className="h-6 w-6 text-indigo-500" />
              <span>Call Menu (IVR)</span>
            </div>
            <p className="text-sm text-gray-500">
              Let callers choose with keypad
            </p>
          </div>
          <button
            className="text-sm text-blue-600 font-medium hover:underline"
            onClick={() => setOpenConfigureSlotModal(true)}
          >
            ↻ Change
          </button>
        </div>
  
        {/* Menu Recording */}
        <div className="space-y-1">
          <div className="flex items-center text-sm font-medium text-gray-700">
            Menu Recording
            <TooltipComponent message={"The recording that plays when callers reach this menu"}/>
          </div>
          <RouteSelect
            value={getRecordingSelectValue(route?.offHoursIvrMenu?.menu_recording)}
            onChange={handleMenuRecordingChange}
            options={recordingOptions}
            placeholder="Select a recording"
            addOption={addMenuRecordingOption}
          />
  
          {route?.offHoursIvrMenu?.menu_recording && (
            <AudioPlayerForRouting
              src={
                recordings.find(
                  (e) => e.M.rec_id.S == route?.offHoursIvrMenu?.menu_recording
                ).M.url.S
              }
            />
          )}
        </div>
  
        <div className="space-y-1">
          <div className="flex items-center text-sm font-medium text-gray-700">
            Hangup Recording
            <TooltipComponent message={""}/>
          </div>
          <RouteSelect
            value={getRecordingSelectValue(route?.offHoursIvrMenu?.hangup_recording)}
            onChange={handleHangupRecordingChange}
            options={recordingOptions}
            placeholder="Select a recording"
            addOption={addHangupRecordingOption}
          />
  
          {route?.offHoursIvrMenu?.hangup_recording && (
            <AudioPlayerForRouting
              src={
                recordings.find(
                  (e) => e.M.rec_id.S == route?.offHoursIvrMenu?.hangup_recording
                ).M.url.S
              }
            />
          )}
        </div>
  
        {/* Menu Options */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm font-medium text-gray-700">
            <div className="flex items-center">
              Menu Options
              <InfoOutlined className="text-gray-400 ml-1" fontSize="small" />
            </div>
            <button
              className="text-blue-600 hover:underline text-sm font-medium flex items-center"
              onClick={() => {
                const opts = route?.offHoursIvrMenu?.options ?? {};
                const nextDigit = getNextAvailableIvrDigit(opts);
                if (!nextDigit) return;
                setRoute({
                  ...route,
                  offHoursIvrMenu: {
                    ...route.offHoursIvrMenu,
                    options: {
                      ...opts,
                      [nextDigit]: {
                        digit: nextDigit,
                        id: `press_${nextDigit}`,
                        name: `Press ${nextDigit}`,
                        primaryAction: null,
                        notification: null,
                      },
                    },
                  },
                });
              }}
            >
              + Add Option
            </button>
          </div>

          {/* Menu option cards: options keyed by DTMF digit ("1", "2", ...) */}
          {route?.offHoursIvrMenu?.options &&
            sortIvrOptionEntries(Object.entries(route.offHoursIvrMenu.options)).map(([optionKey, item], idx) => (
                <OffHoursIvrMenuOption key={optionKey} route={route} setRoute={setRoute} item={item} optionKey={optionKey} index={idx} enableBusinessHours={enableBusinessHours} />
              ))}
        </div>
      </div>
    );
  };
  
  export default OffHoursIvrMenu;
  
  